'use client';

import { ColumnDef } from '@tanstack/react-table';
import { Ellipsis } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Processor } from '@/lib/api/processors';
import { getIntegrationImage } from '@/lib/utils/integration-utils';
import { formatDate } from '@/lib/format';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';

interface GetProcessorsTableColumnsProps {
  onEditProcessor?: (processorId: string) => void;
  onDeleteProcessor?: (processorId: string) => void;
  t: (key: string) => string;
  orgRoutes: any;
}

export function getProcessorsTableColumns({
  onEditProcessor,
  onDeleteProcessor,
  t,
  orgRoutes,
}: GetProcessorsTableColumnsProps): ColumnDef<Processor>[] {
  return [
    // 1. Select checkbox
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label={t('table.selectAll')}
          className="translate-y-0.5"
        />
      ),
      cell: ({ row }) => (
        <DataTableCheckbox row={row} />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 40,
    },
    // 2. Processor Name (primary identifier)
    {
      id: 'name',
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.name')} />,
      cell: ({ row }) => {
        const processor = row.original;
        return (
          <div className="text-sm font-medium">{processor.name}</div>
        );
      },
      enableSorting: false,
      size: 180,
    },
    // 4. Processor Type (with icon)
    {
      id: 'type',
      accessorKey: 'type',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.type')} />,
      cell: ({ row }) => {
        const processor = row.original;
        return (
          <div className="flex items-center gap-2">
            <img
              src={getIntegrationImage(processor.type)}
              alt={processor.type}
              className="h-5 w-5 flex-shrink-0 object-contain"
            />
            <span className="font-medium capitalize">{processor.type}</span>
          </div>
        );
      },
      enableSorting: false,
      size: 120,
    },
    // 4. Status (consistent badge styling)
    {
      id: 'status',
      accessorKey: 'enabled',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.status')} />,
      cell: ({ row }) => {
        const processor = row.original;
        return (
          <Badge variant={processor.enabled ? 'green' : 'gray'} className="min-w-[72px] justify-center">
            {processor.enabled ? t('active') : t('inactive')}
          </Badge>
        );
      },
      enableSorting: false,
      size: 100,
    },
    // 6. Created Date (consistent formatting)
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.created')} />,
      cell: ({ row }) => {
        const processor = row.original;
        const date = new Date(processor.createdAt ?? new Date());
        return (
          <div className="flex flex-col gap-0.5 py-1">
            <div className="text-sm leading-none">{formatDate(date, 'MMM d, yyyy')}</div>
            <div className="text-muted-foreground text-xs leading-none">{formatDate(date, 'h:mm a')}</div>
          </div>
        );
      },
      enableSorting: false,
      size: 130,
    },
    // Processor ID (moved to end, copyable)
    {
      id: 'id',
      accessorKey: 'id',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.processorId')} />,
      cell: ({ row }) => {
        const processor = row.original;
        const displayValue = `${processor.id.substring(0, 8)}...`;
        return (
          <CopyableResourceBadge
            name={t('table.columns.processorId')}
            value={displayValue}
            copyValue={processor.id}
          />
        );
      },
      enableSorting: false,
      enableHiding: true,
      size: 140,
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const processor = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label={t('table.openMenu')}
                variant="ghost"
                className="data-[state=open]:bg-muted flex h-8 w-8 p-0"
                onClick={(e) => e.stopPropagation()}
              >
                <Ellipsis className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[160px]">
              <DropdownMenuItem asChild>
                <Link to={orgRoutes.PROCESSOR_DETAILS(processor.id)}>{t('table.actions.viewDetails')}</Link>
              </DropdownMenuItem>
              {onDeleteProcessor && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteProcessor(processor.id);
                    }}
                    className="text-destructive focus:text-destructive"
                  >
                    {t('table.actions.deleteProcessor')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
      enableSorting: false,
      enableHiding: false,
      size: 40,
    },
  ];
}
